<template>
  <div class="py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <v-btn
          @click="openCreateEditModal()"
          color="primary"
          elevation="0"
        >
          <v-icon left>mdi-plus</v-icon>
          <span class="d-none d-md-flex body-2">
            {{ $t('customer.branches.actions.create') }}
          </span>
        </v-btn>
      </template>
    </h-header>

    <v-card outlined>
      <v-data-table
        disable-sort
        hide-default-footer
        mobile-breakpoint="20"
        :headers="headers"
        :items="branches"
        :loading="isLoading"
        :items-per-page="-1"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div class="d-flex align-center flex-wrap">
            <span class="mr-2">{{ item.name }}</span>
            <v-chip v-if="isHeadquarter(item)" class="grey lighten-2" x-small>
              {{ $t('customer.branches.label.isHeadquarter') }}
            </v-chip>
          </div>
        </template>
        <template v-slot:[`item.city`]="{ item }">
          {{ item.city ? item.city.name : '-' }}
        </template>
        <!-- Clamped to one line: the full formatted address is long enough to blow the row height up to
             ten lines in the client layout, which is narrower than the admin one. -->
        <template v-slot:[`item.address`]="{ item }">
          <span class="branch-list__address" :title="item.address">{{ item.address }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    @click="openCreateEditModal(item.id)"
                    :disabled="isExternallyManaged(item)"
                    icon
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>
                {{
                  isExternallyManaged(item)
                    ? $t('customer.branches.hint.headquarterManagedExternally')
                    : $t('customer.branches.actions.edit')
                }}
              </span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    @click="openConfirmDeleteModal(item)"
                    :disabled="isHeadquarter(item)"
                    icon
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>
                {{
                  isHeadquarter(item)
                    ? $t('customer.branches.hint.headquarterCannotBeDeleted')
                    : $t('customer.branches.actions.delete')
                }}
              </span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <CreateUpdateBranch ref="createModal" />
    <ModalBranchConfirmDelete
      ref="modalBranchConfirmDelete"
      migrates-users-to-headquarter
      @confirm="callbackDelete"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

import CreateUpdateBranch from '@/components/branch/CreateUpdateBranch'
import ModalBranchConfirmDelete from '@/components/branch/ModalBranchConfirmDelete'
import { branchHeaders } from './listHeaders/branchHeaders'

export default {
  name: 'BranchList',

  components: {
    CreateUpdateBranch,
    ModalBranchConfirmDelete,
  },

  data() {
    return {
      headers: branchHeaders,
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'branches']),
  },

  methods: {
    ...mapActions([
      'fetchBranches',
      'fetchOwnBranch',
      'deleteOwnBranch',
      'openModal',
    ]),
    ...mapMutations(['setOwnBranch']),

    // Comes straight from /web/branch/list, so the badge and the disabled delete do not depend on any
    // other endpoint being healthy.
    isHeadquarter(branch) {
      return Boolean(branch.headquarter)
    },

    // Headquarter of a customer replicated from Hauseful Checklist: hcheck owns that record, so an edit
    // here would be replayed over on the next replication event.
    isExternallyManaged(branch) {
      return Boolean(branch.externally_managed)
    },

    openConfirmDeleteModal(branch) {
      this.$refs.modalBranchConfirmDelete.openConfirmModal(branch)
    },

    callbackDelete(branch) {
      this.deleteOwnBranch(branch.id)
    },

    openCreateEditModal(id) {
      this.setOwnBranch({})
      this.openModal('CREATE_UPDATE_OWN_BRANCH_MODAL')
      // /web/branch/list omits phone, email, zipCode and appointmentResumeContact, so the form needs
      // the details endpoint.
      if (id) this.fetchOwnBranch(id).then(() => this.$refs.createModal.fillFields())
    },
  },

  mounted() {
    this.fetchBranches()
  },
}
</script>

<style scoped>
.branch-list__address {
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
